const ItemType = require('@/lib/Items/ItemType');
const { sortByNavOrder } = require('@/functions/arraySorting');

class ItemBarcodeService {
    /**
     * @param {ItemBarcodeRepository} itemBarcodeRepository
     * @param {ItemRepository} itemRepository
     * @param {UserService} userService
     */
    constructor(
        itemBarcodeRepository,
        itemRepository,
        userService,
    ) {
        this.itemBarcodeRepository = itemBarcodeRepository;
        this.itemRepository = itemRepository;
        this.userService = userService;
    }

    /**
     * Finds the item that should be used when the given barcode is scanned.
     *
     * - An item can have multiple barcodes.
     * - The same barcode can be used on multiple items.
     * - All item types can now have barcodes. (Although as of August 2026 only products do.)
     *
     * If multiple items match, we prioritize items that are in the currently selected item branch.
     *
     * @param {string} barcode
     *
     * @returns {Promise<?AbstractItem>}
     */
    async findItemByBarcode(barcode) {
        const itemBranchID = this.userService.getItemBranchID();
        if (!itemBranchID) {
            throw new Error('Cannot find an item by barcode when no item branch is selected.');
        }

        // Find all matching barcodes.
        let barcodeRows = await this.itemBarcodeRepository.findByBarcode(barcode);

        // Item options could theoretically have barcodes now too. But these cannot be directly added
        // to the cart, so exclude those.
        barcodeRows = barcodeRows.filter((barcodeRow) => barcodeRow.itemType !== ItemType.ITEM_OPTION);

        // Load the item for each barcode found.
        let allitems = await Promise.all(
            barcodeRows
                .map(async (barcodeRow) => {
                    return this.itemRepository
                        .getItem(barcodeRow.itemType, barcodeRow.itemID)
                        .catch(() => null);
                })
        );

        // Skip not-found items.
        allitems = allitems.filter((item) => item !== null);

        let eligibleItems = allitems.filter((item) => {
            return item.branchIDs.includes(itemBranchID);
        });

        if (eligibleItems.length < 1) {
            console.debug('No items found in the current branch, falling back to all items with the barcode.');
            // There are no items in the current branch. So use all the items found.
            eligibleItems = allitems;
        }

        // Sort the eligible items by nav order so we can consistently say which one is "first".
        sortByNavOrder(eligibleItems);

        // Return the first eligible item found.
        return eligibleItems.length > 0 ? eligibleItems[0] : null;
    }
}

module.exports = ItemBarcodeService;
